import { type ReactNode, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useValidateGeocodingApiKey } from '@/api/domains/merchant-settings';
import type { GeocodingApiName, GeocodingApiProbeResult } from '@/api/types/settings';
import { Input } from '@/components/ui/input';
import { useDebouncedValue } from '@/hooks/use-debounced-value';

const MIN_GEOCODING_KEY_LENGTH = 10;

type KeyStatus = {
  tone: 'idle' | 'pending' | 'valid' | 'invalid' | 'missing';
  failedApis?: GeocodingApiName[];
  message?: string;
};

type ApiKeyFieldProps = {
  label: string;
  initialValue: string;
  /** Called on blur when the (trimmed) value changed. */
  onSave: (value: string) => void;
  /** Helper text shown under the label (may contain links). */
  description?: ReactNode;
  /** Show the "required" hint when the field is empty. */
  required?: boolean;
  /** Probe the key against the Google Geocoding/Places APIs while typing. */
  validate?: boolean;
};

/** Figma 347-7189: 16px medium label, 12px gap, bordered input. */
export function ApiKeyField({
  label,
  initialValue,
  onSave,
  description,
  required = false,
  validate = false,
}: ApiKeyFieldProps) {
  const { t } = useTranslation('authorization');
  const inputId = useId();
  const [value, setValue] = useState(initialValue);

  // Re-sync when the saved settings arrive/refresh.
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const trimmed = value.trim();
  const debouncedKey = useDebouncedValue(trimmed, 600);
  const savedKey = initialValue.trim();
  const validateKey = useValidateGeocodingApiKey();
  const { mutate: probeKey, reset: resetProbe } = validateKey;

  useEffect(() => {
    if (!validate) return;
    if (debouncedKey === savedKey || debouncedKey.length < MIN_GEOCODING_KEY_LENGTH) {
      resetProbe();
      return;
    }
    probeKey({ key: debouncedKey });
  }, [validate, debouncedKey, savedKey, probeKey, resetProbe]);

  const status: KeyStatus = (() => {
    if (required && trimmed === '') {
      return { tone: 'missing' };
    }
    if (!validate || debouncedKey === '' || debouncedKey.length < MIN_GEOCODING_KEY_LENGTH) {
      return { tone: 'idle' };
    }
    if (validateKey.isPending) {
      return { tone: 'pending' };
    }
    if (validateKey.data?.valid) {
      return { tone: 'valid' };
    }
    if (validateKey.data && !validateKey.data.valid) {
      const failedApis = (
        Object.entries(validateKey.data.apis) as [GeocodingApiName, GeocodingApiProbeResult][]
      )
        .filter(([, probe]) => !probe.valid)
        .map(([name]) => name);
      const firstError = failedApis
        .map((name) => validateKey.data!.apis[name].error_message)
        .find((msg): msg is string => Boolean(msg));

      return { tone: 'invalid', failedApis, message: firstError };
    }
    return { tone: 'idle' };
  })();

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={inputId} className="text-sm font-medium text-black">
        {label}
      </label>
      {description ? <p className="text-[13px] text-muted-foreground">{description}</p> : null}
      <Input
        id={inputId}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => onSave(value)}
        autoComplete="off"
        spellCheck={false}
        className="h-9 rounded-[10px] bg-white"
      />
      {status.tone === 'pending' ? (
        <p className="text-xs text-muted-foreground">{t('keys.checking')}</p>
      ) : null}
      {status.tone === 'valid' ? (
        <p className="text-xs text-emerald-600">{t('keys.valid')}</p>
      ) : null}
      {status.tone === 'missing' ? (
        <p className="text-xs text-destructive">{t('keys.required')}</p>
      ) : null}
      {status.tone === 'invalid' ? (
        <div className="text-xs text-destructive">
          <p>{t('keys.invalid', { message: status.message ?? '' })}</p>
          {status.failedApis && status.failedApis.length > 0 ? (
            <ul className="mt-1 list-disc pl-5">
              {status.failedApis.map((api) => (
                <li key={api}>{t(`keys.apiNames.${api}`)}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
